import { createVirtualFillNode, getSubTreeUids, isCopperLayer, matchLayerIdentifier, pcbLayoutRootNodeId, PcbNodeTypes, } from "@buildwithflux/models";
import { chain, isEqual, set } from "lodash";
import { Box2, Vector2 } from "three";
import { Clipper, ClipperShape } from "../../../../../clipper";
import { geom2, mat3 } from "../../../../../common";
import { calcKeepoutForNodes, canNodeHaveKeepout, CopperFillHandler, createPolygonShape, CurveDivisions, FillCutoutType, fillGetKeepoutMode, getCopperKeepOutZone, getFillCutoutShape, getKeepoutForNodes, nodeHasFillOnLayer, } from "../../../../../fills";
import { getAncestorUidByType, getChildrenOfType, getDescendants, getDescendantsOfType, getNodesOfType, getTopLevelLayoutOfNode, PcbNodeHelper, } from "../../../../../nodes";
import { createLayoutShape } from "../../../../../shapes/primitives/createLayoutShape";
import { getViaSegmentFromType } from "../../../../../smart_via";
import { RuleParser } from "../../../rule_parsing_and_compilation";
import { createTraceGraph } from "../createTraceGraph";
import { FillIslandRemoval } from "./FillIslandRemoval";
/*
    Warning: keep an ibuprofen at hand when working on fills code, these classes are a mess of complexity
*/
const errorKey = "FILL_FAILED";
const notifyUserMessage = "Copper fills were not generated due to an internal error. A high priority alert has been sent " +
    "to the Flux team so we can fix this as soon as possible. Sorry for the inconvenience.";
/**
 * Computes the density of vias along x and y axis of the board. Densities are clamped to a maximum
 * allowable number of vias (based on the board's area, vias size, and a hardcoded factor). Clamping
 * safeguards against excessive number of stitch vias being generated which would otherwise lead to
 * performance issues.
 */
function getClampedDensities(layoutSize, projectedViaSize, density) {
    const boardAreaSide = Math.sqrt(layoutSize.height * layoutSize.width);
    // Hardcoded factor to limit the number of vias generated ~30 vias per axis on a square board
    const maxViasSide = Math.sqrt(900);
    const minDensity = Math.max(boardAreaSide / maxViasSide, projectedViaSize);
    return {
        x: Math.max(density.x, minDensity),
        y: Math.max(density.y, minDensity),
    };
}
function getGridAlignedViasInfo(node, projectedViaSize, layoutShape, polygonShape) {
    if (projectedViaSize <= 0) {
        return null;
    }
    const nodeTransform = mat3.toThreeMatrix3(node.bakedRules.transformRelativeToShapesLayer ?? mat3.makeIdentity());
    // This rule determines both stitching density, and whether to stitch this net at all.
    // Note also that only nets with fills get selected as stitching candidates.
    // We also bail out in case the density is 0, which means that we don't want any stitching via.
    // HACK: net may be unbaked for unknown reason
    // TODO: the reason is a mix of baked and unbaked nodes a passed to the webworker: FLUX-4396
    const density = node.bakedRules?.fillStitchingDensity;
    if (!density || density.x === 0 || density.y === 0) {
        return null;
    }
    const regionBounds = new Box2().setFromPoints(layoutShape.getPoints(CurveDivisions));
    if (polygonShape) {
        const polygonTransformedPoints = polygonShape
            .getPoints(CurveDivisions)
            .map((p) => p.applyMatrix3(nodeTransform));
        const polygonShapeBounds = new Box2().setFromPoints(polygonTransformedPoints);
        regionBounds.intersect(polygonShapeBounds);
    }
    const layoutSize = regionBounds.getSize(new Vector2());
    // A large value of vias makes Flux crash. For this reason we limit the density to a minimum value proportional to the board area.
    // We use a multiple of the projectedViaSize since we don't want it to be smaller than it anyways. The constant multiplier is
    // hardcoded such that it will produce ~300 vias at most with any board size.
    // TODO: notify the user when this clamping happens
    const clampedDensity = getClampedDensities(layoutSize, projectedViaSize, density);
    if (clampedDensity.x <= 0 || clampedDensity.y <= 0) {
        return null;
    }
    const rowRange = Math.ceil(layoutSize.height / clampedDensity.y);
    const colRange = Math.ceil(layoutSize.width / clampedDensity.x);
    return { rowRange, colRange, clampedDensity, regionBounds };
}
function getGridAlignedVias(node, projectedViaSize, obstacleShapes, generatedObstacles, layoutShape, polygonShape, fillShapes) {
    const info = getGridAlignedViasInfo(node, projectedViaSize, layoutShape, polygonShape);
    if (!info)
        return [];
    const { rowRange, colRange, clampedDensity, regionBounds } = info;
    const stitchVias = [];
    const radius = projectedViaSize / 2;
    // Prevents stitching vias from other fills/polygons from overlapping the ones we are
    // generating. This could still happen with polygons, especially if they are on the same net, so
    // we want to make sure we put the other generated fills in the obstacles shapes
    obstacleShapes = [
        ...obstacleShapes,
        ...Object.values(generatedObstacles).map(({ shape }) => [
            shape,
            shape.shapeBounds(),
        ]),
    ];
    // HACK: node may be unbaked for unknown reason
    // TODO: fix 👆 : FLUX-4396
    const offset = node.bakedRules?.fillStitchingOffset ?? { x: 0, y: 0 };
    for (let row = 0; row <= rowRange; row++) {
        for (let col = 0; col <= colRange; col++) {
            // TODO: switch to vec2
            const pos = new Vector2(col * clampedDensity.x, row * clampedDensity.y)
                .add(regionBounds.min)
                .add(new Vector2(offset.x, offset.y));
            // For speed (fewer points to match), we use only the bbox of the via.
            const viaBox = Clipper.shape([
                [
                    // TODO: replace with inline ivector2 ?
                    new Vector2(-radius, radius).add(pos),
                    new Vector2(radius, radius).add(pos),
                    new Vector2(radius, -radius).add(pos),
                    new Vector2(-radius, -radius).add(pos),
                ],
            ]);
            // TODO good candidate for an rtree optimisation here
            if (!hitsObstacle(obstacleShapes, viaBox) && isViaContainedByFills(viaBox, fillShapes)) {
                // A typical project will have a small collection of island vias, which in most cases should cap the
                // time complexity of this check. Island vias are usually only a fraction of total vias in a project
                // compared to stitch vias
                stitchVias.push({
                    type: "grid-aligned",
                    row,
                    col,
                    pos,
                });
            }
        }
    }
    return stitchVias;
}
export default class FillVirtualNodeGenerator {
    ruleCompiler;
    treeManager;
    bakeIndividualNode;
    logger;
    /** keys: {fillParentNodeUid: {obstacleNodeUid: [...]}} */
    static obstacleShapes = {};
    constructor(ruleCompiler, treeManager, bakeIndividualNode, logger) {
        this.ruleCompiler = ruleCompiler;
        this.treeManager = treeManager;
        this.bakeIndividualNode = bakeIndividualNode;
        this.logger = logger;
    }
    generate(updatedNodes, resetCache) {
        // TODO: this typing assumes everything is baked - that's a bad assumption!!! TODO: FIX!!
        // eslint-disable-next-line no-console
        console.time(`FLUX-PERF: fill generation`);
        // TODO: this typing assumes everything is baked
        let treeNodes = this.treeManager.getBakedNodes();
        const nets = getNodesOfType(treeNodes, PcbNodeTypes.net);
        const polygons = getNodesOfType(treeNodes, PcbNodeTypes.polygon);
        const stitchViaSize = this.getViaSize();
        const traceGraph = createTraceGraph(this.treeManager);
        const context = {
            pcbTracingGraph: traceGraph,
            pcbNodes: treeNodes,
            pcbLayoutSelectorMatching: this.ruleCompiler.pcbLayoutSelectorMatching,
        };
        // NOTE on data flow:
        // - ACTUAL document manipulation is carried out using final return values of these variables.
        // - this.treeManager is local to this webworker, manipulating it does not change the document.
        const nodesToRemove = [];
        const nodesToAdd = [];
        const nodesToUpdate = [];
        const shapesMap = {};
        // Helper functions: THE ONLY way to mutate the tree during fill generation.
        // They record changes in the transaction arrays AND apply them via treeManager methods.
        // After each mutation, they refresh snapshots so subsequent code sees the latest state.
        /**
         * Records nodes to be added to transaction and applies the addition to tree.
         * This is the ONLY way to add nodes during fill generation.
         */
        const addNodes = (nodes) => {
            if (nodes.length === 0)
                return;
            // Record in transaction
            nodesToAdd.push(...nodes);
            // Apply and refresh
            this.treeManager.addNodes(nodes);
            treeNodes = this.treeManager.getBakedNodes();
            context.pcbNodes = treeNodes;
        };
        /**
         * Records nodes to be removed from transaction and applies the removal to tree.
         * This is the ONLY way to remove nodes during fill generation.
         */
        const removeNodes = (nodes) => {
            if (nodes.length === 0)
                return;
            // Record in transaction
            nodesToRemove.push(...nodes);
            // Apply and refresh
            this.treeManager.removeNodesByUid(nodes.map((n) => n.uid));
            treeNodes = this.treeManager.getBakedNodes();
            context.pcbNodes = treeNodes;
        };
        /**
         * Records nodes to be updated in transaction and applies the update to tree.
         * This is the ONLY way to update nodes during fill generation.
         */
        const updateNodes = (nodes) => {
            if (nodes.length === 0)
                return;
            // Record in transaction
            nodesToUpdate.push(...nodes);
            // Apply and refresh
            this.treeManager.updateNodes(nodes);
            treeNodes = this.treeManager.getBakedNodes();
            context.pcbNodes = treeNodes;
        };
        /**
         * Bakes nodes and refreshes tree state.
         * This is the ONLY way to bake nodes during fill generation.
         * Always call this after adding/updating nodes to ensure baked properties are visible.
         */
        const bakeNodes = (nodes) => {
            if (nodes.length === 0)
                return;
            // Bake all nodes
            for (const n of nodes) {
                this.bakeIndividualNode(n);
            }
            // Refresh tree to make baked state visible
            treeNodes = this.treeManager.getBakedNodes();
            context.pcbNodes = treeNodes;
        };
        // NOTE: delete any orphan fills and nodes under them. This happens when
        // a ground element is deleted. There is currently no logic to delete
        // the dependent virtual nodes elsewhere. See applyPcbLayoutEnginePatches.
        removeNodes(this.findOrphanFillNodes(nets, polygons));
        // --
        const cache = {
            getCachedLayout: {},
            getCachedFill: {},
            getCachedLayoutShape: {},
            getCachedPolygonShape: {},
        };
        // Performing intersections between simple square bounds, and complex geometry is lightning fast - 40ms for mvniekerk/16xgpioext130
        // --
        const getCachedLayout = (node) => {
            if (cache.getCachedLayout[node.uid]) {
                return cache.getCachedLayout[node.uid] ?? null;
            }
            const layout = getTopLevelLayoutOfNode(treeNodes, node.uid);
            if (!layout) {
                // NOTE: a doc can always have no layouts
                return null;
            }
            cache.getCachedLayout[node.uid] = layout;
            return layout;
        };
        const getCachedLayoutShape = (layout) => {
            if (cache.getCachedLayoutShape[layout.uid]) {
                return cache.getCachedLayoutShape[layout.uid] ?? null;
            }
            const layoutShape = createLayoutShape(layout.bakedRules);
            if (!layoutShape) {
                this.logger.captureError(new Error(`Failed to create layout shape`));
                return null;
            }
            cache.getCachedLayoutShape[layout.uid] = layoutShape;
            return layoutShape;
        };
        const getCachedPolygonShape = (polygon) => {
            if (cache.getCachedPolygonShape[polygon.uid]) {
                return cache.getCachedPolygonShape[polygon.uid] ?? null;
            }
            const polygonShape = createPolygonShape(polygon);
            if (!polygonShape) {
                this.logger.captureError(new Error(`Failed to create fill shape`));
                return null;
            }
            cache.getCachedPolygonShape[polygon.uid] = polygonShape;
            return polygonShape;
        };
        const getCachedFillNode = (node) => {
            // Fill node should now be present in tree after call to updateTreeWithVirtualNodes
            const fillNode = treeNodes[`${node.uid}.fill`];
            if (!fillNode) {
                return null;
            }
            cache.getCachedFill[node.uid] = fillNode;
            return fillNode;
        };
        // --
        const nodesWithFills = this.getNodesThatNeedFill(nets, polygons);
        for (const node of nodesWithFills) {
            shapesMap[node.uid] = {};
        }
        if (resetCache) {
            // if we have to reset the obstacles cache, might as well clean it up
            FillVirtualNodeGenerator.obstacleShapes = {};
        }
        // Pre-cache everything and create fill virtual nodes if needed
        for (const node of nodesWithFills) {
            const layout = getCachedLayout(node);
            if (!layout) {
                continue;
            }
            const layoutShape = getCachedLayoutShape(layout);
            if (!layoutShape) {
                continue;
            }
            if (node.type === PcbNodeTypes.polygon) {
                getCachedPolygonShape(node);
            }
            // Insert fill node for net if it does not exist
            if (!getCachedFillNode(node)) {
                const fill = FillVirtualNodeGenerator.createFillNode(this.treeManager, node);
                const { nodesToAdd: newNodes, nodesToUpdate: updatedNodes } = this.prepareVirtualNodes([fill]);
                // Add nodes to tree first (required for baking)
                addNodes(newNodes);
                updateNodes(updatedNodes);
                // Bake after adding (baking requires nodes to be in tree)
                bakeNodes([...newNodes, ...updatedNodes]);
            }
        }
        const getUserCreatedSmartVias = (net) => {
            const connectedLayersOfNet = net.bakedRules.connectedLayers ?? [];
            const layoutUid = net.bakedRules.topLevelLayoutUid;
            if (!layoutUid) {
                return [];
            }
            const layout = treeNodes[layoutUid];
            if (!layout || layout.type !== PcbNodeTypes.layout) {
                return [];
            }
            const stackUpSortedCopper = Object.values(layout.bakedRules.stackup ?? {})
                .filter(isCopperLayer)
                .sort((a, b) => a.order - b.order);
            /**
             * Get the copper layers that are connected to this via, based on the via's segment from
             * smart via configuration
             */
            const getConnectedLayersOfVia = (node) => {
                if (node.bakedRules.active === false) {
                    return [];
                }
                const smartVia = treeNodes[node.parentUid];
                if (!smartVia || smartVia.type !== PcbNodeTypes.smartVia) {
                    return [];
                }
                const smartViaBakedRules = smartVia.bakedRules;
                const segmentInfo = getViaSegmentFromType(node.bakedRules.viaType, smartViaBakedRules.selectedViaConfigs);
                if (!segmentInfo) {
                    return [];
                }
                const startLayer = segmentInfo.segment.startLayer;
                const endLayer = segmentInfo.segment.endLayer;
                const layers = [];
                for (let layer = startLayer; layer <= endLayer; layer++) {
                    const layerIndex = layer - 1;
                    const layerCopper = stackUpSortedCopper.at(layerIndex);
                    if (layerCopper) {
                        layers.push(layerCopper);
                    }
                }
                return layers;
            };
            /**
             * Get all copper layers that are connected to this vias of this smart via, based on the segment from
             * configuration of this smart via
             */
            const getConnectedLayersOfSmartVia = (node) => {
                const dumbVias = getDescendantsOfType(treeNodes, node, PcbNodeTypes.via);
                const connectedLayers = [];
                for (const via of dumbVias) {
                    connectedLayers.push(...getConnectedLayersOfVia(via));
                }
                return connectedLayers;
            };
            // Get the smart vias of the net
            const netSmartVias = getDescendantsOfType(treeNodes, net, PcbNodeTypes.smartVia);
            const netSmartViasConnected = netSmartVias.filter((smartVia) => {
                if (!smartVia.bakedRules.active || PcbNodeHelper.isVirtualNode(smartVia)) {
                    return false;
                }
                const connectedLayersOfSmartVia = getConnectedLayersOfSmartVia(smartVia);
                // If smart via has no connected layers, it cannot be a source of island copper connectivity so return false
                if (connectedLayersOfSmartVia.length === 0) {
                    return false;
                }
                // If net is connected to all layers in the stackup, we'll need to check the smart via is connected to all
                // copper layers
                if (connectedLayersOfNet.some((connectedLayerOfNet) => connectedLayerOfNet === "All")) {
                    // Every copper layer that the smart via is connected to must have a matching copper layer in the stackup
                    return connectedLayersOfSmartVia.every((connectedLayerOfSmartVia) => stackUpSortedCopper.some((connectedLayerOfNet) => connectedLayerOfSmartVia.uid === connectedLayerOfNet.uid));
                }
                else {
                    // Every copper layer that the smart via is connected to must have a matching copper layer in the net
                    return connectedLayersOfSmartVia.every((connectedLayerOfSmartVia) => connectedLayersOfNet.some((connectedLayerOfNet) => matchLayerIdentifier(connectedLayerOfSmartVia, connectedLayerOfNet)));
                }
            });
            // Transform vias to positions
            return netSmartViasConnected.map((via) => via.bakedRules.positionRelativeToDocument);
        };
        /*
            Stage 0: Pre clean, remove virtual via nodes generated in previous pass. We're expecting these to be replaced during
            subsequent execution
        */
        {
            const viasToRemove = [];
            for (const node of nodesWithFills) {
                for (const fill of getDescendantsOfType(treeNodes, node, PcbNodeTypes.fill)) {
                    for (const via of getDescendantsOfType(treeNodes, fill, PcbNodeTypes.via)) {
                        if (PcbNodeHelper.isVirtualNode(via)) {
                            viasToRemove.push(via);
                        }
                    }
                }
            }
            removeNodes(viasToRemove);
        }
        const expectedNumViasForNodes = Object.fromEntries(nodesWithFills.map((node) => {
            const layout = getCachedLayout(node);
            if (!layout)
                return [node.uid, 0];
            const layoutShape = getCachedLayoutShape(layout);
            if (!layoutShape)
                return [node.uid, 0];
            const polygonShape = node.type === PcbNodeTypes.polygon ? getCachedPolygonShape(node) : null;
            const info = getGridAlignedViasInfo(node, stitchViaSize, layoutShape, polygonShape);
            if (!info)
                return [node.uid, 0];
            return [node.uid, info.rowRange * info.colRange];
        }));
        const nodeAreas = chain(nodesWithFills)
            .map((node) => {
            if (node.type === PcbNodeTypes.polygon) {
                const points = getCachedPolygonShape(node)?.getPoints(CurveDivisions);
                const area = points ? geom2.polygonArea(points) : null;
                return [node.uid, area];
            }
            return [node.uid, null];
        })
            .fromPairs()
            .value();
        // Sorting order is important, since the first fill will displace other fills
        nodesWithFills.sort((a, b) => {
            // Prioritize polygons, then full-net fills
            if (a.type === PcbNodeTypes.net && b.type === PcbNodeTypes.polygon)
                return 1;
            if (a.type === PcbNodeTypes.polygon && b.type === PcbNodeTypes.net)
                return -1;
            // Heuristic: prioritize polygons with smaller area
            if (a.type === PcbNodeTypes.polygon && b.type === PcbNodeTypes.polygon) {
                return (nodeAreas[a.uid] ?? Infinity) - (nodeAreas[b.uid] ?? Infinity);
            }
            // full-net fill vs full-net fill
            if (a.type === PcbNodeTypes.net && b.type === PcbNodeTypes.net) {
                // The sorting criteria here basically says "net with lower density stitch vias are processed first". The
                // rationale is, lower stitch via densities have more space between stitch vias. This means, nets with
                // higher stitch via densities will tend exist between previously processed nets, with stitch vias of lower
                // density
                return (expectedNumViasForNodes[a.uid] ?? 0) - (expectedNumViasForNodes[b.uid] ?? 0);
            }
            // TODO: introduce a new rule for sorting order, overriding heuristics
            return 0;
        });
        // Accumulators
        const generatedFillShapes = new Map();
        const generatedStitchingVias = [];
        const generatedObstacles = [];
        // All fill shapes generated so far, by layer, so we correctly avoid already-computed fills
        const fillShapeObstaclesPerLayer = {};
        for (const node of nodesWithFills) {
            // Get the obstacle shapes for all nodes that stitching should avoid,
            // but only if this board actually has any stitching enabled.
            if (node.bakedRules?.fillStitchingDensity) {
                // Cache the obstacle shapes globally by node uid
                this.getUpdatedObstacleShapes(node, treeNodes, updatedNodes, resetCache, context);
            }
            const layout = getCachedLayout(node);
            if (!layout) {
                continue;
            }
            const layoutShape = getCachedLayoutShape(layout);
            if (!layoutShape) {
                continue;
            }
            const polygonShape = node.type === PcbNodeTypes.polygon ? getCachedPolygonShape(node) : null;
            const fillNode = getCachedFillNode(node);
            if (!fillNode) {
                continue;
            }
            const fillNetUid = node.bakedRules?.effectiveNetId;
            const fillNet = treeNodes[fillNetUid ?? ""];
            if (!fillNet || fillNet.type !== PcbNodeTypes.net) {
                continue;
            }
            // First get the fills WITHOUT the vias. We don't need to cache
            // these in the store because they are only used by the local
            // stitching code and don't need to be shared with other threads.
            // - - - - - - - - - - - - - - - - - - - -
            // Step 1:
            // Get fill shapes for net across all layers
            const fillShapeWithCutoutsByLayerId = this.getFillShapesWithCutouts(layout, node, fillNet.uid, 
            // Filtering updated nodes by this criteria means we can skip unessisary clipperjs cutout of fills for nodes where
            // that work is not actually needed. See example "Pad Dacre: STD 4" on /dacre/island-removal-edge-cases-2?editor=pcb_2d
            FillIslandRemoval.filterUserCreatedNodeOnNetConnectedLayer(fillNet, updatedNodes), layoutShape, polygonShape, fillShapeObstaclesPerLayer, resetCache, context);
            if (!fillShapeWithCutoutsByLayerId) {
                // Don't hard fail - this behavior is inherited behavior that is 'by design', since original fill implementation
                continue;
            }
            // Remove all the generated obstacles (stitching vias) from already-iterated fills/polygons
            for (const layerId of Object.keys(fillShapeWithCutoutsByLayerId)) {
                if (!layout.bakedRules.stackup) {
                    continue;
                }
                const layer = layout.bakedRules.stackup[layerId];
                if (!layer) {
                    continue;
                }
                const shapesToBeRemoved = [];
                for (const obstacle of generatedObstacles) {
                    const obstacleNode = treeNodes[obstacle.nodeUid];
                    if (!obstacleNode || !canNodeHaveKeepout(obstacleNode)) {
                        continue;
                    }
                    const keepoutMode = fillGetKeepoutMode(obstacleNode, treeNodes, layer, fillNet.uid);
                    const keepoutValue = calcKeepoutForNodes(node, obstacleNode, this.ruleCompiler.pcbLayoutSelectorMatching);
                    if (keepoutMode === FillCutoutType.HolePlusKeepout) {
                        const shape = (obstacle.holeShape ?? obstacle.shape).offset(keepoutValue);
                        shapesToBeRemoved.push(shape);
                    }
                    else if (keepoutMode === FillCutoutType.OuterShapePlusKeepout) {
                        const shape = obstacle.shape.offset(keepoutValue);
                        shapesToBeRemoved.push(shape);
                    }
                    obstacle.alreadyRemovedFromFillUids.push(node.uid);
                }
                if (fillShapeWithCutoutsByLayerId[layerId]) {
                    fillShapeWithCutoutsByLayerId[layerId] =
                        fillShapeWithCutoutsByLayerId[layerId].subtract(shapesToBeRemoved);
                }
            }
            // - - - - - - - - - - - - - - - - - - - -
            // Step 2:
            // Get intersection of fill geometry across layers of this net, excluding any intersection regions that are already connected.
            const unconnectedLayerIntersections = FillIslandRemoval.unconnectedIntersectionShapes(context, fillShapeWithCutoutsByLayerId, FillVirtualNodeGenerator.obstacleShapes[node.uid] ?? {}, treeNodes, fillNet);
            // Calculate positions where stitch vias would be placed for this net. If a stitch via is found to overlap intersection geometry
            // then that will be used in place of separately created island vias. This avoids creation of redundant island vias that are not
            // needed due to multi-layer copper connectivity provided by a stitch via(s)
            const stitchViasGridAligned = getGridAlignedVias(node, stitchViaSize, 
            // FillVirtualNodeGenerator.obstacleShapes does not include stitching vias, a) because they are explicitly
            // filtered out by `!isStitchingNode()` in `stitchingShouldAvoidNode()`, and b) because they are currently
            // being generated and haven't been added to data yet. See next comment for how we deal with them.
            Object.values(FillVirtualNodeGenerator.obstacleShapes[node.uid] ?? {}), 
            // Note: mostly, we don't need to explicitly avoid stitching vias, because any stitching from other nets have
            // holes in the current net's fill, and so we won't attempt to place vias there in any case. However, polygons
            // can also generate stitching vias, so we can end up with same-net stitching vias from a polygon in an previous
            // iteration. To account for this, we also need to pass `generatedObstacles` below.
            generatedObstacles, layoutShape, polygonShape, fillShapeWithCutoutsByLayerId);
            // Take into account manual vias and previously created stitching vias
            const manualViaPositions = getUserCreatedSmartVias(fillNet);
            for (const stitchVia of generatedStitchingVias) {
                // Different net
                if (stitchVia.bakedRules.effectiveNetId !== fillNet.uid) {
                    continue;
                }
                // Not active
                if (!stitchVia.bakedRules.active) {
                    continue;
                }
                // For now we support only stitching vias with "All" layers
                // TODO: When we re-enable connected layers for stitching vias, we need to perform proper layer checking here
                const connectedLayersOfVia = stitchVia.bakedRules.connectedLayers ?? [];
                if (connectedLayersOfVia.some((connectedLayerOfVia) => connectedLayerOfVia === "All")) {
                    manualViaPositions.push(stitchVia.bakedRules.positionRelativeToDocument);
                }
            }
            // Positions of vias need to be in local layout space
            const manualViaPositionsLocal = manualViaPositions.map((pos) => PcbNodeHelper.scenePositionToNodeSpace(pos, layout));
            // - - - - - - - - - - - - - - - - - - - -
            // Step 3:
            // Obtain collection of island connection coordinates. These coordinates represent locations on the board where a through
            // hole via would would establish connectivity between fill geometry on all layers
            const stitchViaCandidates = FillIslandRemoval.getIslandViaPositionsFromIntersection(stitchViasGridAligned, manualViaPositionsLocal, unconnectedLayerIntersections, stitchViaSize, node.bakedRules.minTraceWidth);
            // - - - - - - - - - - - - - - - - - - - -
            // Step 4:
            // Build a shape island cache. This is a temporary store of shape island data structures that relate fill shapes and layers.
            // Useful for subsequent steps of the algorithm
            const layerIslands = FillIslandRemoval.getShapeIslands(layout, fillShapeWithCutoutsByLayerId);
            // - - - - - - - - - - - - - - - - - - - -
            // Step 5:
            // Obtain a subset of pad and/or via nodes, on this net, that are potential sources of connectivity between shape islands
            const potentiallyConnectingNetChildren = FillIslandRemoval.getPotentiallyConnectingNetChildren(treeNodes, fillNet);
            // - - - - - - - - - - - - - - - - - - - -
            // Step 6:
            // Obtain the final set of multi-layer islands. A PCB can consist of many islands (disconnected copper fill shapes, on a
            // particular layer). An island is typically more complex though, connecting copper fill across multiple layers. These
            // 'multilayer islands' are found by clustering islands by common (connecting) island connection coordinates and net
            // connecting nodes (applicable pads and vias). Any multi-layer islands that aren't found to connect to the net, are
            // discarded (which fulfills the 'island removal' behavior - see following steps)
            const singleLayer = !node.bakedRules.connectedLayers?.includes("All") && node.bakedRules.connectedLayers?.length === 1;
            const islandClusters = FillIslandRemoval.getIslandClusters(context, layerIslands, potentiallyConnectingNetChildren, stitchViaCandidates, stitchViaSize, manualViaPositionsLocal, singleLayer);
            // - - - - - - - - - - - - - - - - - - - -
            // Step 7:
            // Find which islands, if any, are known electrically connected to the net (eg connecting to a terminal)
            const netElectricallyConnectedShapeIslands = FillIslandRemoval.getIslandsElectricallyConnectedToNet(context, layerIslands, fillNet, node.type === PcbNodeTypes.polygon);
            // - - - - - - - - - - - - - - - - - - - -
            // Step 8:
            // Filter multi-layer islands by those that contain islands known to be electrically connected to the net.
            // Any islands that are omitted at this point will be 'removed' from the fill
            const shapeMultiLayerIslandsOnNet = islandClusters.filter((island) => Array.from(island).some((shape) => netElectricallyConnectedShapeIslands.has(shape)));
            // - - - - - - - - - - - - - - - - - - - -
            // Step 9:
            // Filter out any via positions that aren't inside at least one multi-layer fill island.
            const viaIslandPositions = FillIslandRemoval.getViaPositions(shapeMultiLayerIslandsOnNet, stitchViaCandidates);
            // - - - - - - - - - - - - - - - - - - - -
            // Step 11:
            // Insert and remove island vias
            const viaIslandTransaction = FillIslandRemoval.createIslandVias(fillNode, node, viaIslandPositions, node.bakedRules.connectedLayers ?? []);
            // Step 12: Add stitching vias to tree, then bake them
            // Note: Must add to tree BEFORE baking (baking needs tree lookup)
            addNodes(viaIslandTransaction);
            bakeNodes(viaIslandTransaction);
            // Step 13: Gather some data about the vias for later processing
            generatedStitchingVias.push(...viaIslandTransaction);
            // Remove the footprint of the vias from the intersection geometry and obstacles
            for (const stitchVia of viaIslandTransaction) {
                const shapeForViaOnLayer = getFillCutoutShape(stitchVia, node, FillCutoutType.OuterShapePlusKeepout, CurveDivisions, context, true);
                const shapeForViaOnLayerHole = getFillCutoutShape(stitchVia, node, FillCutoutType.HolePlusKeepout, CurveDivisions, context, true);
                if (shapeForViaOnLayer) {
                    generatedObstacles.push({
                        nodeUid: stitchVia.uid,
                        shape: shapeForViaOnLayer,
                        holeShape: shapeForViaOnLayerHole,
                        alreadyRemovedFromFillUids: [],
                    });
                }
            }
            // Step 14: Merge all the islands into a single fill shape per layer
            const nodeFills = FillIslandRemoval.mergeIslandsToFills(shapeMultiLayerIslandsOnNet);
            generatedFillShapes.set(node, nodeFills);
            // Stores the shapes for multiple fills/polygons intersection
            for (const [layerId, shape] of Object.entries(nodeFills)) {
                fillShapeObstaclesPerLayer[layerId] ??= [];
                fillShapeObstaclesPerLayer[layerId]?.push({ parentNodeUid: node.uid, shape });
            }
        }
        // Stage 2: Go back to the other generated fill shapes and subtract the newly added stitching vias
        // This is necessary otherwise they will short, as they are added after the fill for a particular net is generated
        // TODO: This is a bit hacky and O(n^3)
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        generatedFillShapes.forEach((nodeFillsPerLayer, node) => {
            const netForFill = node.type === PcbNodeTypes.net
                ? node
                : treeNodes[getAncestorUidByType(treeNodes, node.uid, PcbNodeTypes.net) ?? ""];
            if (!netForFill) {
                return;
            }
            const layout = getCachedLayout(node);
            if (!layout) {
                return;
            }
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            Object.entries(nodeFillsPerLayer).forEach(([layerId, fillShape]) => {
                const layer = layout.bakedRules.stackup?.[layerId];
                if (!layer) {
                    return;
                }
                const allObstaclesToRemoveShape = [];
                generatedObstacles
                    .filter((o) => !o.alreadyRemovedFromFillUids.includes(node.uid))
                    // eslint-disable-next-line @buildwithflux/no-array-foreach
                    .forEach((obstacle) => {
                    const obstacleNode = treeNodes[obstacle.nodeUid];
                    if (!obstacleNode || !canNodeHaveKeepout(obstacleNode)) {
                        return;
                    }
                    const keepoutMode = fillGetKeepoutMode(obstacleNode, treeNodes, layer, netForFill.uid);
                    const keepoutValue = calcKeepoutForNodes(node, obstacleNode, this.ruleCompiler.pcbLayoutSelectorMatching);
                    if (keepoutMode === FillCutoutType.HolePlusKeepout) {
                        const shape = (obstacle.holeShape ?? obstacle.shape).offset(keepoutValue);
                        allObstaclesToRemoveShape.push(shape);
                    }
                    else if (keepoutMode === FillCutoutType.OuterShapePlusKeepout) {
                        const shape = obstacle.shape.offset(keepoutValue);
                        allObstaclesToRemoveShape.push(shape);
                    }
                });
                if (allObstaclesToRemoveShape.length > 0) {
                    nodeFillsPerLayer[layerId] = fillShape.subtract(allObstaclesToRemoveShape);
                }
            });
        });
        // Final stage: finalize the fill shapes and store them in the shapesMap
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        generatedFillShapes.forEach((nodeFillsPerLayer, node) => {
            const layout = getCachedLayout(node);
            if (!layout) {
                return;
            }
            const fillByLayer = {};
            // eslint-disable-next-line @buildwithflux/no-array-foreach
            Object.entries(nodeFillsPerLayer).forEach(([layerId, fillShape]) => {
                // Same as ClipperShape.toThreeJSShapes(), but returns an array of arrays of Vector2 instead
                // since the Shape creation is already happening in polygonsToShapes()
                const shapes = fillShape.separateShapes();
                fillByLayer[layerId] = shapes.map((s) => s.toPolygons()).flat();
            });
            set(shapesMap, [node.uid, layout.uid], fillByLayer);
        });
        // Note: generatedStitchingVias are already in nodesToAdd via addNodes() helper calls
        // No need to add them again here
        // eslint-disable-next-line no-console
        console.timeEnd(`FLUX-PERF: fill generation`);
        // NOTE: returns changes that should actually be made to the document.
        // Changing the document is handled in the hand-off from webworker back to the main thread.
        return { nodesToAdd, nodesToUpdate, nodesToRemove, shapesMap };
    }
    /**
     * Finds orphaned fill nodes and their descendants.
     * Orphan fills occur when their parent net/polygon no longer needs a fill.
     * Does NOT delete them - caller must use removeNodes() helper.
     */
    findOrphanFillNodes(nets, polygons) {
        const treeNodes = this.treeManager.getBakedNodes();
        const validNodeWithFillsUids = new Set(this.getNodesThatNeedFill(nets, polygons).map((n) => n.uid));
        const orphanedNodes = [];
        // eslint-disable-next-line @buildwithflux/no-array-foreach
        getNodesOfType(treeNodes, PcbNodeTypes.fill).forEach((node) => {
            const parent = treeNodes[node.parentUid];
            if (!parent ||
                ((parent.type === PcbNodeTypes.net || parent.type === PcbNodeTypes.polygon) &&
                    !validNodeWithFillsUids.has(parent.uid))) {
                // Collect orphaned fill nodes and their descendants
                // Actual removal is done by the caller via removeNodes() helper
                orphanedNodes.push(node);
                orphanedNodes.push(...getDescendants(treeNodes, node));
                this.logger.captureError(new Error(`Found orphaned fill node ${node.name} ${node.uid}`));
            }
        });
        return orphanedNodes;
    }
    getNodesThatNeedFill(nets, polygons) {
        function hasConnectedLayers(connectedLayers) {
            // Return false if no layers are specified
            connectedLayers ??= [];
            return !(connectedLayers.length === 0 || (connectedLayers.length === 1 && connectedLayers[0] === ""));
        }
        const treeNodes = this.treeManager.getBakedNodes();
        // Build a set of all node UIDs reachable from ROOT via childUids traversal.
        // Nodes not in this set are "orphaned" - they exist in pcbLayoutNodes but are not
        // reachable from the root, typically due to their parent's childUids being overwritten.
        // NOTE: We use treeManager.treeNodes (all nodes) instead of getBakedNodes() because
        // the ROOT node doesn't have bakedRules and would be excluded from getBakedNodes(),
        // causing getSubTreeUids to fail to traverse the tree.
        const reachableUids = new Set(getSubTreeUids({ pcbNodes: this.treeManager.treeNodes, nodeUid: pcbLayoutRootNodeId, includeRoot: true }));
        return [
            ...nets.filter((net) => {
                // Skip orphaned nets that are not reachable from ROOT via childUids
                if (!reachableUids.has(net.uid)) {
                    this.logger.captureError(new Error(`Net ${net.name} ${net.uid} is orphaned (not reachable from ROOT). Excluding from fill generation.`));
                    return false;
                }
                // TODO: getAncestorUidByType is actually adding up here... use a
                // cache or just check id string or something
                const isNetUnderSubLayout = Boolean(getAncestorUidByType(treeNodes, net.uid, PcbNodeTypes.element));
                if (isNetUnderSubLayout) {
                    return false;
                }
                // HACK: net may be unbaked for unknown reason
                if (!net.bakedRules) {
                    this.logger.captureError(new Error(`Net ${net.name} ${net.uid} has no baked rules! Excluding from fill generation.`));
                    return false;
                }
                return hasConnectedLayers(net.bakedRules.connectedLayers);
            }),
            ...polygons.filter((polygon) => {
                // Skip orphaned polygons that are not reachable from ROOT via childUids
                if (!reachableUids.has(polygon.uid)) {
                    this.logger.captureError(new Error(`Polygon ${polygon.name} ${polygon.uid} is orphaned (not reachable from ROOT). Excluding from fill generation.`));
                    return false;
                }
                // This may seem redudant given the polygon's "baked node" type - it is nessisary however, due to general
                // uncertainty we have on the baked state of nodes passed to Fill Generation. We cannot entirely trust the
                // typing here, as shown to be the case with FLUX-7546
                return (PcbNodeHelper.isBakedNode(polygon) &&
                    polygon.bakedRules.polygonShape &&
                    polygon.bakedRules.active &&
                    hasConnectedLayers(polygon.bakedRules.connectedLayers));
            }),
        ];
    }
    getViaSize() {
        // QUESTION: what guarantee is there that there will always be a via
        // rule set? and that no other rules that determine the actual via size
        // of a baked node?
        // TODO: fix this, seems to be a bug where rule set that selects stitch vias is not affecting the size - the
        // size value doesn't correctly propagate through to here, preventing stitch density being affected by user
        // specified via sizing. All that happens is the `DEFAULT_VIA_SIZE` value is used
        const viaRules = this.ruleCompiler.getRuleSetForExactSelectorAndKey("via", "size"); //
        const viaSize = viaRules ? new RuleParser(viaRules).parseRules(["size"])?.size?.x : undefined;
        if (viaSize === undefined) {
            throw new Error("No default via size, or invalid default via size, specified. This should never happen; if it does, it means there's a bug with our hardcoded fallback.");
        }
        return viaSize;
    }
    /**
     * Prepares virtual nodes for insertion: determines which are new vs existing
     * and merges data for existing nodes.
     * Does NOT add to tree, does NOT bake - caller must add node using `addNodes(..)` then bake using `bakeNodes(..)`.
     */
    prepareVirtualNodes(virtualNodes) {
        const treeNodes = this.treeManager.getBakedNodes();
        const existingNodes = virtualNodes.filter((n) => n.uid && n.uid in treeNodes);
        const newNodes = virtualNodes.filter((n) => !(n.uid && n.uid in treeNodes));
        const nodesToUpdate = [];
        const nodesToAdd = [];
        // Prepare updates for existing nodes by merging new data (don't bake yet)
        for (const nodeData of existingNodes) {
            const existingNode = treeNodes[nodeData.uid];
            if (!existingNode)
                continue;
            // Create updated node by merging changes
            const updatedNode = { ...existingNode };
            let changed = false;
            for (const fieldName in nodeData) {
                const key = fieldName;
                if (key === "type" || key === "uid") {
                    continue; // Never update these
                }
                if (!isEqual(updatedNode[key], nodeData[key])) {
                    updatedNode[key] = nodeData[key];
                    changed = true;
                }
            }
            if (changed) {
                nodesToUpdate.push(updatedNode);
            }
        }
        // Prepare new nodes (don't add or bake yet)
        for (const node of newNodes) {
            nodesToAdd.push(node);
        }
        return {
            nodesToAdd,
            nodesToUpdate,
        };
    }
    // eslint-disable-next-line @typescript-eslint/member-ordering
    static createFillNode(treeManager, parentNode) {
        const treeNodes = treeManager.treeNodes;
        const fillUid = `${parentNode.uid}.fill`;
        const childFill = getChildrenOfType(treeNodes, parentNode.uid, PcbNodeTypes.fill)[0];
        const treeFill = treeNodes[fillUid];
        const existingFill = childFill ?? treeFill;
        const fillNode = createVirtualFillNode(parentNode.uid, parentNode.uid, {
            parentUid: parentNode.uid,
            uid: existingFill?.uid ?? fillUid,
            childUids: existingFill ? existingFill.childUids : [],
        });
        // See if this fill is orphaned in the tree nodes and if so add it as a child of the net.
        if (treeFill && !childFill) {
            // We have an existing fill node associated with this net, but the fill is not a child of the net.
            // add it to the net's child list so that we can update it correctly.
            treeManager.updatePcbLayoutNode(parentNode.uid, (netNode) => {
                netNode.childUids.push(treeFill.uid);
            });
        }
        return fillNode;
    }
    getUpdatedObstacleShapes(fillParentNode, treeNodes, updatedNodes, resetCache, context) {
        if (resetCache) {
            FillVirtualNodeGenerator.obstacleShapes[fillParentNode.uid] = this.getObstacleShapes(fillParentNode, treeNodes, context);
        }
        else {
            const record = FillVirtualNodeGenerator.obstacleShapes[fillParentNode.uid] ?? {};
            FillVirtualNodeGenerator.obstacleShapes[fillParentNode.uid] = record;
            Object.assign(record, this.getObstacleShapes(fillParentNode, updatedNodes, context));
        }
    }
    // TODO afaict `getObstacleShapes` is specifically about getting the obstacles to stitch vias
    // (i.e. not the fill cutout shapes or whatever). This is judging from the
    // .filter(stitchingShouldAvoidNode()) in the code below.
    //
    // HOWEVER, we appear to mingle these concepts in confusing ways -- specifically,
    // FillIslandRemoval.unconnectedIntersectionShapes() cuts out these obstacle shapes again, from
    // a shape that afaict already has the *fill cutouts* removed, previously, by CopperFillHandler,
    // using the correct
    //
    // So we should prob rename this function to clarify it's about stitching obstacles only (if I'm
    // right), and we should investigate the above concern further and fix it.
    getObstacleShapes(fillParentNode, pcbNodesToGetShapesFrom, context) {
        const obstaclesFromExistingNodes = chain(pcbNodesToGetShapesFrom)
            .values()
            .filter((node) => PcbNodeHelper.stitchingShouldAvoidNode(node, fillParentNode))
            .map((n) => {
            const keepOut = getKeepoutForNodes(n, fillParentNode, context.pcbLayoutSelectorMatching);
            // Use very low vertex-count for collision detection, so the path offset operation inside getCopperKeepoutZone doesn't have to work too hard.
            const shape = getCopperKeepOutZone(n, CurveDivisions, context, keepOut);
            return [n.uid, [shape, shape?.shapeBounds()]];
        })
            // Ensure shape exists (getCopperKeepOutZone above can return undefined for some nodes,
            // eg nodes outside a Layout node, because they have no positionRelativeToRootLayout)
            .filter((v) => !!v[1]?.[0])
            .fromPairs()
            .value();
        return obstaclesFromExistingNodes;
    }
    // This geometry will be suitable for connectivity detection between fills, via Vias
    // We will need to apply a second pass to it to cut out vias
    getFillShapesWithCutouts(layout, node, netUid, updatedNodes, layoutShape, polygonShape, polygonObstaclesPerLayer, resetCache, context) {
        try {
            // HACK: net may be unbaked for unknown reason
            const minTraceWidth = node.bakedRules.minTraceWidth ?? 0;
            const spacingFromLayoutEdge = layout.bakedRules.boardInsetMargin ?? 0;
            const stackup = Object.values(layout.bakedRules.stackup ?? {});
            const fillLayers = stackup.filter((l) => nodeHasFillOnLayer(node, l));
            const fillShapes = {};
            for (const layer of fillLayers) {
                const fillHandler = CopperFillHandler.getHandlerForNodeLayer(layer, node.uid, this.logger);
                const polygonObstaclesForLayer = polygonObstaclesPerLayer[layer.uid] ?? [];
                const polygonObstaclesForLayerWithKeepout = [];
                // Accumulate the other fill shapes to be avoided by this fill with a keepout
                for (const { shape, parentNodeUid } of polygonObstaclesForLayer) {
                    const otherParentNode = context.pcbNodes[parentNodeUid];
                    const isFillOrPolygon = otherParentNode?.type === PcbNodeTypes.fill || otherParentNode?.type === PcbNodeTypes.polygon;
                    const sameNet = isFillOrPolygon && otherParentNode.bakedRules.effectiveNetId === netUid;
                    if (!sameNet && otherParentNode && canNodeHaveKeepout(otherParentNode)) {
                        const keepout = calcKeepoutForNodes(node, otherParentNode, this.ruleCompiler.pcbLayoutSelectorMatching) ??
                            0;
                        const fillShapeWithKeepout = shape.offset(keepout);
                        polygonObstaclesForLayerWithKeepout.push(fillShapeWithKeepout);
                    }
                }
                const accumulatorMaskForLayer = Clipper.shape([]).union(polygonObstaclesForLayerWithKeepout);
                fillShapes[layer.uid] = fillHandler.getUpdatedFillShape(updatedNodes, context, layoutShape, polygonShape, accumulatorMaskForLayer, spacingFromLayoutEdge, node, netUid, minTraceWidth, resetCache);
            }
            return fillShapes;
        }
        catch (error) {
            this.logger.captureError(error, {
                notifyUserMessage,
                errorKey,
            });
            return null;
        }
    }
}
function isViaContainedByFills(viaBox, fillShapes) {
    let count = 0;
    for (const fillShape of Object.values(fillShapes)) {
        if (viaBox.areVerticesContainedIn(fillShape)) {
            count += 1;
        }
        if (count >= 2) {
            return true;
        }
    }
    return false;
}
function hitsObstacle(
// WARNING: bounds in int space
obstacleShapes, viaBox) {
    const viaBounds = viaBox.shapeBounds();
    return obstacleShapes.some(([s, b]) => ClipperShape.boundsOverlap(viaBounds, b) && viaBox.intersect(s).totalArea() > 0);
}
//# sourceMappingURL=FillVirtualNodeGenerator.js.map